import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import MiiaFwConfig from '@/configs/config'
import type { FeatureOptions } from '@/types/api/common/governmentSetting/queryGovernmentSettingResponseParam'
import { useGovernmentSettingApi } from '@/api/common/governmentSetting/governmentSetting'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

export const useGovernmentSettingStore = defineStore('governmentSettingStore', () => {
  const { query } = useGovernmentSettingApi()

  /** 自治体設定読み込みフラグ（読み込み完了後にtrue） */
  const isLoaded = ref(false)
  /** 機能オプション */
  const featureOptions = ref<FeatureOptions[]>([])

  /**
   * 自治体設定の取得してStoreに保存する
   * @param govCode 自治体コード
   */
  const fetchAndSetGovernmentSetting = async (govCode: string) => {
    try {
      isLoaded.value = false
      const result = await query({ system: MiiaFwConfig.config.sysId, govCode })
      featureOptions.value = result.featureOptions ?? []
      isLoaded.value = true
    } catch (e) {
      if (e instanceof GlobalError) {
        throw e.addErrorCodeFirst(ERROR_CODES._811100_QUERY_GOVERNMENT_SETTING_ERROR)
      }
      throw new GlobalError(ERROR_CODES._811100_QUERY_GOVERNMENT_SETTING_ERROR, {
        message: String(e)
      })
    }
  }

  /**
   * 自治体の機能オプションに含まれているか判定する
   * @param option 機能オプション
   * @returns 含まれていればtrue
   */
  const hasAuth = (option: FeatureOptions) => featureOptions.value.includes(option)

  /** 自治体設定が読み込まれるまで待機する */
  const waitLoad = async () => {
    if (isLoaded.value) {
      return isLoaded.value
    }
    return new Promise<boolean>(resolve =>
      watch(isLoaded, value => (value ? resolve(value) : undefined))
    )
  }

  /** Storeの状態をリセットする */
  const reset = () => {
    isLoaded.value = false
    featureOptions.value.splice(0)
  }

  return {
    fetchAndSetGovernmentSetting,
    hasAuth,
    waitLoad,
    reset
  }
})
